/**
 * Environment Configuration Helper
 * This module provides access to runtime environment variables injected by the server
 * It falls back to process.env for local development when window._env_ is not available
 */

/**
 * Get environment variable value with fallback to process.env
 * @param {string} key - The environment variable key (e.g., 'REACT_APP_LANG_SECRET_KEY')
 * @param {string} defaultValue - Optional default value if not found
 * @returns {string|undefined} The environment variable value
 */
export const getEnvVar = (key, defaultValue = undefined) => {
  
  // First, try to get from runtime-injected config (production)
  if (typeof window !== 'undefined' && window._env_ && window._env_[key]) {
    return window._env_[key];
  }

  // Fallback to process.env (development/build-time)
  if (process.env[key]) {
    return process.env[key];
  }

  // Return default value if provided
  return defaultValue;
};

/**
 * Get all environment variables that start with a prefix
 * @param {string} prefix - The prefix to filter by (e.g., 'REACT_APP_')
 * @returns {Object} Object containing all matching environment variables
 */
export const getEnvVarsByPrefix = (prefix) => {
  const envVars = {};

  // Check runtime-injected config first
  if (typeof window !== 'undefined' && window._env_) {
    Object.keys(window._env_).forEach(key => {
      if (key.startsWith(prefix)) {
        envVars[key] = window._env_[key];
      }
    });
  }

  // Also check process.env for development
  if (Object.keys(envVars).length === 0) {
    Object.keys(process.env).forEach(key => {
      if (key.startsWith(prefix)) {
        envVars[key] = process.env[key];
      }
    });
  }

  return envVars;
};

/**
 * Check if running in production mode
 * @returns {boolean} True if in production
 */
export const isProduction = () => {
  const nodeEnv = getEnvVar('NODE_ENV', 'development');
  return nodeEnv === 'production';
};

/**
 * Get API URL with proper fallback
 * @param {string} type - The type of URL ('PUBLIC', 'PRIVATE', 'ROOT_PUBLIC')
 * @returns {string} The API URL
 */
export const getApiUrl = (type) => {
  const key = `REACT_APP_${type}_URL`;
  return getEnvVar(key, '');
};

/**
 * Debug function to log all available environment variables (non-sensitive only)
 */
export const debugEnvVars = () => {
  if (!isProduction()) {
    console.group('Environment Variables Debug');
    console.log('Runtime config available:', typeof window !== 'undefined' && !!window._env_);

    if (typeof window !== 'undefined' && window._env_) {
      console.log('Runtime variables:', Object.keys(window._env_).filter(key => !key.includes('SECRET')));
    }

    console.log('Process.env variables:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_') && !key.includes('SECRET')));
    console.groupEnd();
  }
};

// Export specific environment variables for convenience
export const ENV = {

  // Feature flags
  NODE_ENV: getEnvVar('NODE_ENV', 'development'),

  // Helper methods
  getEnvVar,
  getEnvVarsByPrefix,
  isProduction,
  getApiUrl,
  debugEnvVars
};

export default ENV;